import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, X, ChevronDown } from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useBrands } from '../hooks/useBrands';
import { getCategoryUrl, getBrandUrl } from '../utils/url-helpers';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';

function Filters({ filters, onFilterChange, onClearFilters, hideVendorFilter = false }) {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { data: categoriesData } = useCategories();
  const { data: brandsData } = useBrands();

  // Local state for price inputs to avoid API call on every keystroke
  const [localMinPrice, setLocalMinPrice] = useState(filters.minPrice || '');
  const [localMaxPrice, setLocalMaxPrice] = useState(filters.maxPrice || '');
  const debounceRef = useRef(null);

  // Sync local state when filters change externally (e.g. clear filters)
  useEffect(() => {
    setLocalMinPrice(filters.minPrice || '');
    setLocalMaxPrice(filters.maxPrice || '');
  }, [filters.minPrice, filters.maxPrice]);

  const debouncedFilterChange = useCallback((newFilters) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilterChange(newFilters);
    }, 400);
  }, [onFilterChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Filter out categories and brands with 0 products
  const categories = (categoriesData?.data?.categories || []).filter(cat => cat.productCount > 0);
  const brands = (brandsData?.data?.brands || []).filter(brand => brand.productCount > 0);

  const hasActiveFilters = filters.category || filters.brand || filters.brand_slug || filters.minPrice || filters.maxPrice;

  // Resolve the brand dropdown's selected value. BrandPage sends brand_slug (the indexed,
  // diacritic-safe filter) instead of the brand name, so resolve slug -> name here using the
  // brands list we already fetched. Keeps the dropdown highlight working on brand pages.
  const selectedBrandName =
    filters.brand || (filters.brand_slug ? (brands.find(b => b.slug === filters.brand_slug)?.name ?? '') : '');

  const sortOptions = useMemo(() => [
    { value: 'name', label: t('nameAZ') },
    { value: 'name_desc', label: t('nameZA') || 'Name (Z-A)' },
    { value: 'price_asc', label: t('priceLowToHigh') },
    { value: 'price_desc', label: t('priceHighToLow') },
    { value: 'newest', label: t('newestFirst') },
    { value: 'popular', label: t('mostPopular') || 'Most Popular' },
    { value: 'vendor_count', label: t('mostVendors') || 'Most Vendors' }
  ], [language]);

  const handleCategoryChange = useCallback((e) => {
    if (e.target.value) {
      const selectedCategory = categories.find(c => c.name === e.target.value);
      const url = selectedCategory ? getCategoryUrl(selectedCategory) : `/category/${encodeURIComponent(e.target.value)}`;
      navigate(url);
    } else {
      navigate('/');
    }
  }, [categories, navigate]);

  const handleBrandChange = useCallback((e) => {
    if (e.target.value) {
      const selectedBrand = brands.find(b => b.name === e.target.value);
      const url = selectedBrand ? getBrandUrl(selectedBrand) : `/brand/${encodeURIComponent(e.target.value)}`;
      navigate(url);
    } else {
      navigate('/');
    }
  }, [brands, navigate]);

  const handleSortChange = useCallback((e) => {
    onFilterChange({ ...filters, sort: e.target.value });
  }, [onFilterChange, filters]);

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      {/* Mobile filter toggle - Accessibility: added aria-label, aria-expanded, aria-controls; Mobile: increased touch target */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden w-full flex items-center justify-between mb-4 p-4 rounded-lg hover:bg-gray-50 transition-colors min-h-[48px]"
        aria-label={isOpen ? t('closeFilters') || 'Close filters' : t('openFilters') || 'Open filters'}
        aria-expanded={isOpen}
        aria-controls="filter-content"
      >
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5" aria-hidden="true" />
          <span className="font-medium">{t('filters')}</span>
          {hasActiveFilters && (
            <span className="badge bg-primary-100 text-primary-700">{t('active') || 'Active'}</span>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {/* Filter content */}
      <div id="filter-content" className={`space-y-6 ${isOpen ? 'block' : 'hidden md:block'}`}>
        {/* Category filter */}
        {categories.length > 0 && (
          <div>
            <label htmlFor="filter-category" className="block text-sm font-medium text-gray-700 mb-2">
              {t('category')}
            </label>
            <select
              id="filter-category"
              value={filters.category || ''}
              onChange={handleCategoryChange}
              className="w-full input-field"
            >
              <option value="">{t('allCategories')}</option>
              {categories.map((cat) => (
                <option key={cat.slug || cat.name} value={cat.name}>
                  {cat.name} ({cat.productCount})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Brand filter */}
        {!hideVendorFilter && brands.length > 0 && (
          <div>
            <label htmlFor="filter-brand" className="block text-sm font-medium text-gray-700 mb-2">
              {t('brand')}
            </label>
            <select
              id="filter-brand"
              value={selectedBrandName}
              onChange={handleBrandChange}
              className="w-full input-field"
            >
              <option value="">{t('allBrands')}</option>
              {brands.slice(0, 50).map((brand) => (
                <option key={brand.slug || brand.name} value={brand.name}>
                  {brand.name} ({brand.productCount})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Price range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" id="price-range-label">
            {t('priceRangeLabel')}
          </label>
          <div className="space-y-2">
            <input
              type="number"
              placeholder={t('minPrice')}
              aria-label={t('minPrice')}
              value={localMinPrice}
              onChange={(e) => {
                const value = e.target.value;
                setLocalMinPrice(value);
                debouncedFilterChange({ ...filters, minPrice: value });
              }}
              className="w-full input-field"
              min="0"
            />
            <input
              type="number"
              placeholder={t('maxPrice')}
              aria-label={t('maxPrice')}
              value={localMaxPrice}
              onChange={(e) => {
                const value = e.target.value;
                setLocalMaxPrice(value);
                debouncedFilterChange({ ...filters, maxPrice: value });
              }}
              className="w-full input-field"
              min="0"
            />
          </div>
        </div>

        {/* Enhanced Sort */}
        <div>
          <label htmlFor="filter-sort" className="block text-sm font-medium text-gray-700 mb-2">
            {t('sortBy')}
          </label>
          <select
            id="filter-sort"
            value={filters.sort || 'name'}
            onChange={handleSortChange}
            className="w-full input-field"
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="w-full btn-secondary flex items-center justify-center space-x-2"
          >
            <X className="w-4 h-4" />
            <span>{t('clearFilters')}</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default Filters;
